import classNames from 'classnames'
import type { FocusEvent, KeyboardEvent, MouseEvent } from 'react'
import { cloneElement, isValidElement, useCallback, useId, useRef, useState } from 'react'
import { CheckCircleIcon, ListItem, ListItemBody } from '../../components'
import { Dropdown } from '../../components/dropdown'
import { useSelectorKeyDown } from './hooks'
import styles from './Selector.module.scss'
import type { SelectorOption, SelectorProps, ValidTriggerProps } from './types'

const Selector = ({
  className,
  onChange,
  options,
  open: openProp = false,
  selectedValue,
  title,
  trigger,
}: SelectorProps) => {
  const [open, setOpen] = useState(openProp)
  const idListBox = useId()
  const optionsContainerRef = useRef<HTMLDivElement>(null)
  const optionsRefs = useRef<HTMLDivElement[]>([])
  const { focusedIndex, handleTriggerKeyDown } = useSelectorKeyDown({
    open,
    setOpen,
    options,
    optionsRefs,
    selectedValue,
  })

  const handleOptionClicked = useCallback(
    (option: SelectorOption) => {
      onChange && onChange(option)
      setOpen(false)
    },
    [onChange]
  )

  const handleTriggerBlur = (e: FocusEvent<Element>) => {
    // Prevent executing the onBlur if the event comes from the Options
    if (
      optionsContainerRef.current &&
      optionsContainerRef.current.contains(e.relatedTarget as Node)
    ) {
      return
    }
    setOpen(false)
  }

  const handleTriggerClick = () => setOpen(!open)

  const TriggerCloned =
    isValidElement<ValidTriggerProps>(trigger) &&
    cloneElement(trigger, {
      onClick: (e: MouseEvent<Element>) => {
        trigger.props.onClick && trigger.props.onClick(e)
        handleTriggerClick()
      },
      onKeyDown: (e: KeyboardEvent<Element>) => {
        trigger.props.onKeyDown && trigger.props.onKeyDown(e)
        handleTriggerKeyDown(e)
      },
      onBlur: (e: FocusEvent<Element>) => {
        trigger.props.onBlur && trigger.props.onBlur(e)
        handleTriggerBlur(e)
      },
      role: 'combobox',
      'aria-controls': idListBox,
      'aria-expanded': open,
      'aria-activedescendant':
        focusedIndex !== null ? `${idListBox}-${options[focusedIndex]?.value}` : undefined,
    })

  return (
    <div className={classNames('pintxo-selector', className)}>
      <Dropdown
        trigger={TriggerCloned}
        isOpen={open}
        onClose={() => setOpen(false)}
        closeOnClickOutside
        title={title}
        role="listbox"
        id={idListBox}
      >
        <div className={styles['pintxo-selector__options-container']} ref={optionsContainerRef}>
          {options.map((option, index) => (
            <ListItem
              aria-selected={selectedValue === option.value}
              actionable={true}
              key={option.value}
              leadingContent={option.accessory}
              onClick={() => handleOptionClicked(option)}
              role="option"
              trailingContent={selectedValue === option.value ? <CheckCircleIcon /> : undefined}
              ref={(el) => {
                optionsRefs.current[index] = el!
              }}
              id={`${idListBox}-${option.value}`}
              focusedFake={focusedIndex === index}
              tabIndex={-1}
            >
              <ListItemBody title={option.label} />
            </ListItem>
          ))}
        </div>
      </Dropdown>
    </div>
  )
}

export default Selector
